import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';

interface EntitiesManagerState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
}

const state: EntitiesManagerState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: []
};

export const ITEMS_LIST = 'GET_ENTITIES_LIST';

const COLUMNS: any = {
    id: 'ent.id',
    name: 'ent.name',
    position: 'ent.position',
    tax_rate_id: 'ent.tax_rate_id',
    tax_rate_name: 'vr.name',
    created_at: 'ent.created_at',
    updated_at: 'ent.updated_at'
};

function build_query(query: any, page_size?: number) {
    page_size = page_size ? page_size : 20;
    const filter: any[] = [];
    if (query.name) {
        filter.push({ "$ilike": ["ent.name", {"$": `%${query.name}%`}] })
    }
    return {
        "columns": COLUMNS,
        from: 's:entities',
        from_as: 'ent',
        joins: [
            {
                join: 's:vat_rates',
                type: 'left',
                as: 'vr',
                on: {
                    $eq: ['vr.id', 'ent.tax_rate_id']
                }
            }
        ],
        page_size: page_size,
        ...({page: parseInt(query.page) || 1}),
        ...({
            order: [
                { $asc: ['ent.position'] },
                { $asc: ['ent.name'] }
            ]
        }),
        ...(
            filter.length > 1 ?
            {
                "where": {
                    "$and": filter
                }
            } : {
                "where": filter[0]
            }
        )
    }
}

export const actions: ActionTree<EntitiesManagerState, RootState> = {
    loadData({ commit }, query) {
        const vm = (this as any)._vm;
        console.debug('[EntitiesManager.loadData] START', { query });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(ITEMS_LIST)
                .raw('documents.list')
                .action(build_query(query))
            ]).then(((result: ServerResult) => {
                commit('SET_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                commit('LOADED_SUCCESS');

                resolve();
            })).catch((error: any) => {
                console.error('[EntitiesManager.loadData] ERROR', { query, error });
                commit('LOADED_ERROR');
                reject(error);
            });
        })
    },

    saveEntity({ commit }, data) {
        const vm = (this as any)._vm;

        const query = data.id ?
            vm.$q('SAVE_ENTITY').update(data.id, data) :
            vm.$q('SAVE_ENTITY').create('s:entities', data);

        return new Promise((resolve, reject) => {
            vm.$db([
                query
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('SAVE_ENTITY');

                if (operationResult.hasError()) {
                    console.error('[EntitiesManager.saveEntity] FAIL', { data });
                    reject();
                } else {
                    console.info('[EntitiesManager.saveEntity] SUCCESS', { id: data.id });
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('[EntitiesManager.saveEntity] ERROR', { data, error });
                reject(error);
            });
        });
    }
};

export const mutations: MutationTree<EntitiesManagerState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = data.documents;

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    }
};

const getters = {

};

export const STORE_KEY = '$_entities_manager';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
